import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../services/api';
import { Calendar, Clock, User, Phone, MapPin, X, Plus, ChevronLeft, ChevronRight, Check, AlertCircle, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const STATUS_CONFIG = {
  agendada:  { label: 'Agendada',   bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  cancelada: { label: 'Cancelada',  bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500' },
  realizada: { label: 'Realizada',  bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500' },
  reagendada:{ label: 'Reagendada', bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.agendada;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function NovaVisitaModal({ isOpen, onClose, onSave, disponibilidade, loadingDisp, data, setData }) {
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', hora: '', empreendimento: '', observacao: '' });
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  if (!isOpen) return null;

  const slotsDisponiveis = disponibilidade?.slots?.filter(s => s.disponivel) || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.hora) { setErro('Nome e horário são obrigatórios.'); return; }
    setErro('');
    setSaving(true);
    try {
      await onSave({ ...form, data });
      setForm({ nome: '', email: '', telefone: '', hora: '', empreendimento: '', observacao: '' });
      onClose();
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao criar visita');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Calendar size={20} className="text-primary" /> Nova Visita ao Decorado</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
              <input type="date" value={data} onChange={e => setData(e.target.value)} min={new Date().toISOString().slice(0,10)}
                className="w-full border rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Horário *</label>
              {loadingDisp ? (
                <div className="w-full border rounded-lg py-2 px-3 text-sm text-gray-400">Carregando...</div>
              ) : (
                <select value={form.hora} onChange={e => setForm(p => ({...p, hora: e.target.value}))}
                  className="w-full border rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                  <option value="">Selecione</option>
                  {slotsDisponiveis.map(s => <option key={s.hora} value={s.hora}>{s.hora}h</option>)}
                  {slotsDisponiveis.length === 0 && <option disabled>Sem horários disponíveis</option>}
                </select>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Cliente *</label>
            <input value={form.nome} onChange={e => setForm(p=>({...p, nome:e.target.value}))} placeholder="Nome completo"
              className="w-full border rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input type="email" value={form.email} onChange={e => setForm(p=>({...p, email:e.target.value}))} placeholder="email@exemplo.com"
                className="w-full border rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
              <input value={form.telefone} onChange={e => setForm(p=>({...p, telefone:e.target.value}))} placeholder="55449..."
                className="w-full border rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Empreendimento</label>
            <input value={form.empreendimento} onChange={e => setForm(p=>({...p, empreendimento:e.target.value}))} placeholder="Ex: Cores do Parque"
              className="w-full border rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
            <textarea value={form.observacao} onChange={e => setForm(p=>({...p, observacao:e.target.value}))} rows={2} placeholder="Informações adicionais..."
              className="w-full border rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
          </div>
          {erro && <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2"><AlertCircle size={16} />{erro}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
              {saving ? 'Agendando...' : 'Agendar Visita'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Check-in da visita de cliente de corretor parceiro (Portal do Corretor, 2c): a equipe mostra este QR
// no decorado; o corretor lê com o celular (ou digita o código) e a visita vira "realizada".
function CheckinQrModal({ visita, onClose }) {
  const { data, error, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['checkin-qr', visita.id],
    queryFn: () => api.post(`/agenda/visitas/${visita.id}/checkin-qr`).then(r => r.data.data),
    retry: false, refetchOnWindowFocus: false, gcTime: 0,
  });
  const erro = error?.response?.data?.error || (error && 'Não foi possível gerar o QR.');
  const expira = data && new Date(data.expira_em).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-gray-900">Check-in da visita</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">{visita.attendee_nome} · corretor {visita.portal_corretor_nome}</p>
        {isLoading && <p className="text-sm text-gray-400 py-16">Gerando…</p>}
        {erro && <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{erro}</p>}
        {data && (
          <>
            <div className="inline-block p-3 bg-white border rounded-xl"><QRCodeSVG value={data.url} size={220} /></div>
            <p className="text-sm text-gray-600 mt-4">O corretor aponta a câmera do celular para o QR<br />ou digita o código no portal:</p>
            <p className="text-3xl font-bold tracking-[0.3em] text-gray-900 mt-2 font-mono">{data.codigo}</p>
            <p className="text-xs text-gray-400 mt-3">Vale até {expira} · uso único</p>
            <button onClick={() => refetch()} disabled={isFetching} className="mt-4 text-xs text-blue-600 hover:underline">
              {isFetching ? 'Gerando…' : 'Gerar outro (invalida este)'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function VisitasAgenda() {
  const queryClient = useQueryClient();
  const hoje = new Date().toISOString().slice(0, 10);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [dataModal, setDataModal] = useState(hoje);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [checkinVisita, setCheckinVisita] = useState(null);

  const { data: visitas, isLoading } = useQuery({
    queryKey: ['visitas', mes, ano],
    queryFn: () => api.get(`/agenda/visitas?mes=${mes}&ano=${ano}`).then(r => r.data.data),
    refetchInterval: 30000,
  });

  const { data: dispData, isLoading: loadingDisp } = useQuery({
    queryKey: ['disponibilidade', dataModal],
    queryFn: () => api.get(`/agenda/disponibilidade?data=${dataModal}`).then(r => r.data.data),
    enabled: isModalOpen,
  });

  const criarMutation = useMutation({
    mutationFn: (body) => api.post('/agenda/visitas', body).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['visitas'] }),
  });

  const cancelarMutation = useMutation({
    mutationFn: (id) => api.post(`/agenda/visitas/${id}/cancelar`).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['visitas'] }),
  });

  const meses = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  const prevMes = () => { if (mes === 1) { setMes(12); setAno(a => a - 1); } else setMes(m => m - 1); };
  const nextMes = () => { if (mes === 12) { setMes(1); setAno(a => a + 1); } else setMes(m => m + 1); };

  const visitasPorData = (visitas || []).reduce((acc, v) => {
    const d = new Date(v.start_time).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' });
    acc[d] = (acc[d] || []).concat(v);
    return acc;
  }, {});

  const totalAgendadas = (visitas || []).filter(v => v.status === 'agendada').length;
  const totalMes = (visitas || []).length;

  return (
    <div className="space-y-6">
      {/* Header stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="text-2xl font-bold text-blue-600">{totalAgendadas}</div>
          <div className="text-sm text-gray-500 mt-0.5">Visitas agendadas</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="text-2xl font-bold text-gray-800">{totalMes}</div>
          <div className="text-sm text-gray-500 mt-0.5">Total no mês</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="text-2xl font-bold text-green-600">
            {(visitas || []).filter(v => v.status === 'realizada').length}
          </div>
          <div className="text-sm text-gray-500 mt-0.5">Realizadas</div>
        </div>
      </div>

      {/* Nav mês + botão novo */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={prevMes} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ChevronLeft size={18} /></button>
          <span className="text-lg font-semibold text-gray-800 w-40 text-center">{meses[mes-1]} {ano}</span>
          <button onClick={nextMes} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ChevronRight size={18} /></button>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
        >
          <Plus size={16} /> Nova Visita
        </button>
      </div>

      {/* Lista de visitas */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading && <div className="p-12 text-center text-gray-400">Carregando visitas...</div>}
        {!isLoading && (!visitas || visitas.length === 0) && (
          <div className="p-12 text-center text-gray-400">
            <Calendar size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">Nenhuma visita em {meses[mes-1]}</p>
            <p className="text-sm mt-1">Clique em "Nova Visita" para agendar</p>
          </div>
        )}
        {visitas && visitas.length > 0 && (
          <div>
            {Object.entries(visitasPorData).map(([data, items]) => (
              <div key={data}>
                <div className="px-6 py-2.5 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{data}</span>
                </div>
                {items.map((v) => {
                  const hora = new Date(v.start_time).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
                  return (
                    <div key={v.id} className="flex items-center gap-4 px-6 py-4 border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                      <div className="flex-shrink-0 w-14 text-center">
                        <div className="text-lg font-bold text-gray-800">{hora}h</div>
                        <div className="text-xs text-gray-400">60 min</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{v.attendee_nome}</span>
                          <StatusBadge status={v.status} />
                          {v.portal_corretor_nome && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">corretor {v.portal_corretor_nome}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          {v.attendee_phone && <span className="flex items-center gap-1"><Phone size={12} /> {v.attendee_phone}</span>}
                          {v.empreendimento && <span className="flex items-center gap-1"><MapPin size={12} /> {v.empreendimento}</span>}
                        </div>
                      </div>
                      {v.portal_corretor_id && v.status === 'agendada'
                        && new Date(v.start_time).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) === new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }) && (
                        <button onClick={() => setCheckinVisita(v)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-700 rounded-lg transition-colors"
                          title="QR para o corretor validar a visita">
                          <QrCode size={15} /> QR check-in
                        </button>
                      )}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {v.status === 'agendada' && (
                          <button
                            onClick={() => { if (window.confirm(`Cancelar visita de ${v.attendee_nome}?`)) cancelarMutation.mutate(v.id); }}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Cancelar visita"
                          >
                            <X size={16} />
                          </button>
                        )}
                        {v.cal_uid && (
                          <a
                            href={`https://agenda.minhainbase.com.br/booking/${v.cal_uid}`}
                            target="_blank" rel="noreferrer"
                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors text-xs font-medium"
                            title="Ver no cal.diy"
                          >
                            Cal
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {checkinVisita && <CheckinQrModal visita={checkinVisita} onClose={() => setCheckinVisita(null)} />}

      <NovaVisitaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={(body) => criarMutation.mutateAsync(body)}
        disponibilidade={dispData}
        loadingDisp={loadingDisp}
        data={dataModal}
        setData={setDataModal}
      />
    </div>
  );
}
